import React, { useEffect, useState } from 'react';
import useIsMobile from '../hooks/useIsMobile';

const emptyForm = {
  chain_id: '',
  property_id: '',
  module_id: '',
  override_price_monthly: '',
  override_price_yearly: '',
  notes: '',
};

function SubscriptionPriceOverrides({ token }) {
  const { isMobile } = useIsMobile();
  const headers = (extra = {}) => ({ Authorization: `Bearer ${token}`, ...extra });

  const [overrides, setOverrides] = useState([]);
  const [chains, setChains] = useState([]);
  const [properties, setProperties] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });

  const loadOverrides = async () => {
    const res = await fetch('/api/v1/subscription/admin/price-overrides', { headers: headers() });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to load overrides');
    return Array.isArray(data) ? data : data.overrides || [];
  };

  const loadChains = async () => {
    const res = await fetch('/api/v1/core/chains', { headers: headers() });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to load chains');
    return Array.isArray(data) ? data : data.chains || [];
  };

  const loadModules = async () => {
    const res = await fetch('/api/v1/subscription/admin/modules', { headers: headers() });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to load modules');
    return Array.isArray(data) ? data : data.modules || [];
  };

  const loadAll = async () => {
    setLoading(true);
    setError('');
    try {
      const [o, c, m] = await Promise.all([loadOverrides(), loadChains(), loadModules()]);
      setOverrides(o);
      setChains(c);
      setModules(m);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, [token]);

  /* Load properties when chain changes */
  const loadProperties = async (chainId) => {
    if (!chainId) { setProperties([]); return; }
    try {
      const res = await fetch(`/api/v1/core/chains/${chainId}/properties`, { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load properties');
      setProperties(Array.isArray(data) ? data : data.properties || []);
    } catch (e) {
      setProperties([]);
    }
  };

  const onChainChange = (chainId) => {
    setForm((f) => ({ ...f, chain_id: chainId, property_id: '' }));
    loadProperties(chainId);
  };

  const fmtPrice = (v) => {
    const n = Number(v);
    return isNaN(n) || v === null || v === undefined || v === '' ? '—' : `₹${n.toFixed(2)}`;
  };

  const openAdd = () => {
    setForm({ ...emptyForm });
    setShowForm(true);
    setProperties([]);
    setError('');
    setSuccess('');
  };

  const cancelForm = () => {
    setShowForm(false);
    setForm({ ...emptyForm });
    setProperties([]);
  };

  const saveOverride = async () => {
    setError('');
    setSuccess('');
    if (!form.chain_id || !form.module_id) {
      return setError('Chain and Module are required.');
    }
    if (!form.override_price_monthly && !form.override_price_yearly) {
      return setError('At least one override price is required.');
    }
    try {
      const res = await fetch('/api/v1/subscription/admin/price-overrides', {
        method: 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          chain_id: Number(form.chain_id),
          property_id: form.property_id ? Number(form.property_id) : null,
          module_id: Number(form.module_id),
          override_price_monthly: form.override_price_monthly ? Number(form.override_price_monthly) : null,
          override_price_yearly: form.override_price_yearly ? Number(form.override_price_yearly) : null,
          notes: form.notes.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      setSuccess('Price override created.');
      cancelForm();
      loadAll();
    } catch (e) {
      setError(e.message);
    }
  };

  const deleteOverride = async (id) => {
    if (!window.confirm('Delete this price override?')) return;
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`/api/v1/subscription/admin/price-overrides/${id}`, {
        method: 'DELETE',
        headers: headers(),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Delete failed');
      }
      setSuccess('Override deleted.');
      loadAll();
    } catch (e) {
      setError(e.message);
    }
  };

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  /* ── Styles ── */
  const thStyle = {
    padding: '0.6rem 0.75rem', textAlign: 'left', position: 'sticky', top: 0,
    background: '#f5f6fa', borderBottom: '2px solid var(--theme-border, #dcdde1)',
    whiteSpace: 'nowrap', fontSize: '0.82rem', fontWeight: 600,
  };
  const tdStyle = { padding: '0.55rem 0.75rem', borderBottom: '1px solid #eef2f7', fontSize: '0.85rem' };
  const btnPrimary = {
    padding: '0.5rem 1.1rem', background: 'var(--theme-primary, #2563eb)', color: '#fff',
    border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem',
  };
  const btnSecondary = {
    padding: '0.5rem 1.1rem', background: '#dcdde1', color: '#2f3640',
    border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem',
  };
  const btnDanger = {
    padding: '0.3rem 0.7rem', background: '#e74c3c', color: '#fff',
    border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem',
  };
  const inputStyle = {
    padding: '0.5rem 0.6rem', border: '1px solid var(--theme-border, #ccc)',
    borderRadius: 6, fontSize: '0.85rem', width: '100%', boxSizing: 'border-box',
  };
  const labelStyle = { fontWeight: 600, fontSize: '0.8rem', marginBottom: 2, display: 'block', color: '#34495e' };

  return (
    <div className="page-shell">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.8rem' }}>
        <h2 style={{ color: 'var(--theme-primary-dark, #1e3a5f)', margin: 0 }}>Price Overrides</h2>
        <button style={btnPrimary} onClick={openAdd}>+ Add Override</button>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {success && <div className="success-banner">{success}</div>}

      {/* ── Add Panel ── */}
      {showForm && (
        <div className="card" style={{ marginBottom: '1rem', padding: '1rem' }}>
          <h3 style={{ margin: '0 0 0.8rem', color: 'var(--theme-primary-dark, #1e3a5f)' }}>New Price Override</h3>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '0.75rem' }}>
            <div>
              <label style={labelStyle}>Chain *</label>
              <select style={inputStyle} value={form.chain_id} onChange={(e) => onChainChange(e.target.value)}>
                <option value="">-- Select Chain --</option>
                {chains.map((c) => <option key={c.id || c.chain_id} value={c.id || c.chain_id}>{c.chain_name || c.name}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Property (optional)</label>
              <select style={inputStyle} value={form.property_id} onChange={(e) => set('property_id', e.target.value)} disabled={!form.chain_id}>
                <option value="">-- All Properties --</option>
                {properties.map((p) => <option key={p.id || p.property_id} value={p.id || p.property_id}>{p.property_name || p.name}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Module *</label>
              <select style={inputStyle} value={form.module_id} onChange={(e) => set('module_id', e.target.value)}>
                <option value="">-- Select Module --</option>
                {modules.map((m) => <option key={m.id} value={m.id}>{m.module_name} ({m.module_group})</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Override Monthly Price (₹)</label>
              <input style={inputStyle} type="number" min="0" step="0.01" value={form.override_price_monthly} onChange={(e) => set('override_price_monthly', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Override Yearly Price (₹)</label>
              <input style={inputStyle} type="number" min="0" step="0.01" value={form.override_price_yearly} onChange={(e) => set('override_price_yearly', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Notes</label>
              <input style={inputStyle} value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Reason for override" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.9rem' }}>
            <button style={btnPrimary} onClick={saveOverride}>Create Override</button>
            <button style={btnSecondary} onClick={cancelForm}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── Table ── */}
      {loading && <div style={{ color: '#636e72', marginBottom: '0.8rem' }}>Loading...</div>}

      <div className="card" style={{ overflowX: 'auto', maxHeight: '70vh' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Chain</th>
              <th style={thStyle}>Property</th>
              <th style={thStyle}>Module</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Monthly</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Yearly</th>
              <th style={thStyle}>Notes</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {overrides.map((o) => (
              <tr key={o.id} style={{ borderTop: '1px solid #eef2f7' }}>
                <td style={tdStyle}>{o.chain_name || o.chain_id}</td>
                <td style={tdStyle}>{o.property_name || o.property_id || <span style={{ color: '#95a5a6' }}>All</span>}</td>
                <td style={tdStyle}>{o.module_name || o.module_key || o.module_id}</td>
                <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>{fmtPrice(o.override_price_monthly)}</td>
                <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>{fmtPrice(o.override_price_yearly)}</td>
                <td style={{ ...tdStyle, maxWidth: 200, whiteSpace: 'normal' }}>{o.notes || '—'}</td>
                <td style={tdStyle}>
                  <button style={btnDanger} onClick={() => deleteOverride(o.id)}>Delete</button>
                </td>
              </tr>
            ))}
            {!overrides.length && !loading && (
              <tr><td colSpan={7} style={{ padding: '1rem', color: '#7f8c8d', textAlign: 'center' }}>No price overrides found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default SubscriptionPriceOverrides;
